import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Divider,
  TextField,
  Button,
  InputBase,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Container
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Person as PersonIcon, ViewInAr as ViewInArIcon, Save as SaveIcon, Search as SearchIcon } from '@mui/icons-material';

const UserProfile = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');
  const userData = JSON.parse(localStorage.getItem('userData'));
  const [formData, setFormData] = useState({
    name: userData?.user_name || '',
    email: userData?.email || '',
    phone: userData?.number || ''
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleVMFixturesClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
    navigate('/main/vm-fixtures');
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    // Here you would typically make an API call to update the user's profile
    console.log('Saving profile data:', formData);
  };

  const handleSidebarItemClick = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderProfileSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: '#0A2647', fontWeight: 600 }}>
        Edit Profile
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleFormChange} margin="normal" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleFormChange} margin="normal" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleFormChange} margin="normal" />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );

  const renderSidebar = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Box */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            p: '2px 8px'
          }}
        >
          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1 }} />
          <InputBase
            placeholder="Search"
            sx={{
              color: 'white',
              '& input::placeholder': {
                color: 'rgba(255, 255, 255, 0.7)',
                opacity: 1
              },
              flex: 1
            }}
          />
        </Box>
      </Box>

      {/* Sidebar List Items */}
      <List sx={{ flex: 1, px: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleSidebarItemClick('profile')}
            selected={activeSection === 'profile'}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.15)'
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" sx={{ color: 'white' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleVMFixturesClick}
            sx={{
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.15)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <ViewInArIcon />
            </ListItemIcon>
            <ListItemText primary="Fixtures" sx={{ color: 'white' }} />
          </ListItemButton>
        </ListItem>
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="caption" sx={{ color: '#ccc' }}>
          Powered by Neophyte
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5', width: '100vw' }}>
      {/* Left Sidebar */}
      <Box component="nav" sx={{ width: { sm: 280 }, flexShrink: { sm: 0 } }} aria-label="sidebar">
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              bgcolor: '#0A2647',
              color: 'white',
              top: '58px',
              height: 'calc(100vh - 58px)'
            },
          }}
        >
          {renderSidebar()}
        </Drawer>

        {/* Desktop Permanent Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              bgcolor: '#0A2647',
              color: 'white',
              top: '64px',
              height: 'calc(100vh - 64px)'
            }
          }}
          open
        >
          {renderSidebar()}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 3 },
          mt: { xs: '12px', sm: '64px' }, // Much less top margin on mobile
          width: '100%',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          transition: 'padding 0.2s, margin 0.2s',
        }}
      >
        {/* Floating Sidebar Button for Mobile */}
        {isMobile && !mobileOpen && (
          <IconButton
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              position: 'fixed',
              top: '40%', // Centered vertically
              left: 0,
              zIndex: 1301,
              bgcolor: '#0A2647',
              color: 'white',
              borderRadius: '0 50% 50% 0', // Half-oval shape
              width: 28,
              height: 60,
              display: { xs: 'flex', sm: 'none' },
              justifyContent: 'flex-end', // Align icon to the right side
              paddingRight: '4px',
              boxShadow: '3px 0 8px rgba(0,0,0,0.2)',
              '&:hover': {
                transform: 'translateY(-4px)',
                transition: 'transform 0.2s ease-in-out'
              },
              // transition: 'all 0.3s ease',
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18, color: 'black' }} />
          </IconButton>
        )}

        <Container maxWidth="md" disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              bgcolor: '#f5f5f5', // Match background for unified look
              borderRadius: 2,
              width: '100%',
              boxShadow: 'none',
            }}
          >
            {renderProfileSection()}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default UserProfile;
